import { ColDef, GridOptions, ColGroupDef } from '@ag-grid-community/core';
import { Tooltip, TooltipPosition } from '@creditsights/styleguide-web';
import { FundamentalAttExtended } from '../../models/FundamentalMetrics';
import { NumericCellEditor } from './components/NumericCellEditor';
import { convertToBoolean } from './RPCalculator.logic';

export const getRPCalculatorColumns = (currency?: string): ColDef<FundamentalAttExtended>[] => [
  {
    field: 'group',
    rowGroup: true,
    hide: true,
  },
  {
    colId: 'label',
    field: 'label',
    headerName: `Fundamental (${currency})`,
    sortable: false,
    resizable: false,
  },
  {
    colId: 'originalValue',
    field: 'value',
    headerName: 'Original',
    headerClass: 'header-centered',
    sortable: false,
    resizable: false,
    width: 86,
    valueFormatter: (param) => param.value?.toLocaleString('en-US'),
  },
  {
    colId: 'modifiedValue',
    headerName: 'Modified',
    headerClass: 'header-centered',
    cellClass: 'modified-value',
    sortable: false,
    resizable: false,
    width: 86,
    valueFormatter: (param) => param.value?.toLocaleString('en-US'),
    editable: (params) => convertToBoolean(params.data?.value),
    cellEditor: NumericCellEditor,
    suppressPaste: false,
    valueSetter: (params) => {
      // eslint-disable-next-line no-param-reassign
      params.data.modifiedValue = params.newValue;
      return true;
    },
    valueGetter: (params) => params.data?.modifiedValue,
  },
];

export const calculatedValuesColumns: (ColDef | ColGroupDef)[] = [
  {
    headerName: 'Input type',
    colId: 'type',
    field: 'label',
    resizable: false,
    sortable: false,
    headerClass: 'adjusted-header-cell',
    minWidth: 172,
    width: 172,
  },
  {
    headerName: 'Original',
    headerClass: 'border-left-table-cell header-centered',
    children: [
      {
        field: 'original.value',
        headerName: 'Value',
        headerClass: 'border-left-table-cell',
        sortable: false,
        resizable: false,
        valueFormatter: (param) => param.value?.toLocaleString('en-US'),
        minWidth: 0,
        flex: 2,
      },
      {
        field: 'original.percentage',
        headerName: 'P-Tile',
        headerClass: 'border-left-table-cell ',
        resizable: false,
        sortable: false,
        valueFormatter: (param) => param.value?.toLocaleString('en-US'),
        minWidth: 60,
        width: 60,
        cellRenderer: (params) => (
          <Tooltip content={params.data.original?.tooltip} position={TooltipPosition.BottomRight}>
            <div>{params.value}</div>
          </Tooltip>
        ),
      },
    ],
  },
  {
    headerName: 'Calculated',
    headerClass: 'border-left-table-cell header-centered',
    children: [
      {
        field: 'modified.value',
        headerName: 'Value',
        headerClass: 'border-left-table-cell',
        resizable: false,
        sortable: false,
        valueFormatter: (param) => param.value?.toLocaleString('en-US'),
        minWidth: 0,
        flex: 2,
      },
      {
        field: 'modified.percentage',
        headerName: 'P-Tile',
        headerClass: 'border-left-table-cell',
        resizable: false,
        sortable: false,
        valueFormatter: (param) => param.value?.toLocaleString('en-US'),
        minWidth: 60,
        width: 60,
      },
    ],
  },
];

export const RPCalculatorGridOptions: GridOptions<FundamentalAttExtended> = {
  groupDisplayType: 'groupRows',
  groupDefaultExpanded: 1,
  groupRowRendererParams: {
    suppressCount: true,
  },
};

export const RPCalculatorAutoGroupColDef: ColDef<FundamentalAttExtended> = {
  cellRendererParams: {
    suppressCount: true,
    checkbox: true,
  },
};

export const NON_FINANCIAL_MODEL_SECTORS = [
  'Aerospace / Defense',
  'Airlines',
  'Automotive',
  'Capital Goods',
  'Chemicals',
  'Construction Aggregates',
  'Consumer Products and Services',
  'Diversified Conglomerates',
  'Food & Beverages',
  'Gaming / Leisure',
  'Healthcare / Pharmaceuticals',
  'Homebuilding',
  'Independent Power Producers',
  'Media and Cable',
  'Metals and Mining',
  'Oil and Gas',
  'Paper and Packaging',
  'Pipelines / MLP',
  'Retail / Grocers',
  'Shipping and Infrastructure',
  'Technology',
  'Telecommunications',
  'Trucking and Rails',
  'Utilities',
];
